import { HttpParams } from '@angular/common/http';
import { computed, Injectable, Signal, signal } from '@angular/core';
import { produce } from 'immer';
import { Subject } from 'rxjs';
import { FilterOptions, FilterSetting, SortingSetting } from '@coin/shared/core/util-models';
import { TinyHelpers } from '@coin/shared/core/util-helpers';
import { FilterSortHelper } from '../helpers/filter-sort.helper';

const STATE_DEFAULTS: FilterSortStateModel = {
  selectedFilterOptions: [],
  selectedSortOption: null,
  isReloadNeeded: false
};
interface FilterSortStateModel {
  selectedFilterOptions: FilterSetting[];
  selectedSortOption: SortingSetting;
  isReloadNeeded: boolean;
}
// Auto-migrated from NGXS
@Injectable({ providedIn: 'root' })
export class FilterSortState {
  /** Emits the keys to invalidate and reload, or `undefined` to invalidate all filter options */
  public invalidateFilterOptionsSuccessful$ = new Subject<string[] | undefined>();

  private readonly filterSortStateModel = signal<FilterSortStateModel>(STATE_DEFAULTS);

  public selectedFilterOptions = computed(() => this.filterSortStateModel().selectedFilterOptions);

  public selectedFilterKeys = computed(() =>
    this.filterSortStateModel().selectedFilterOptions?.map(filter => filter.key)
  );

  public isReloadNeeded = computed(() => this.filterSortStateModel().isReloadNeeded);

  public getFilterText = computed(() =>
    FilterSortHelper.getFilterQuery(this.filterSortStateModel().selectedFilterOptions)
  );

  public getSortAndFilterText = computed(() => {
    const state = this.filterSortStateModel();
    let filterSortText = '';
    filterSortText += this.getFilterText();

    if (state.selectedSortOption?.key) {
      filterSortText += `&Sorting.Property=${state.selectedSortOption.key}&Sorting.OrderBy=${encodeURIComponent(state.selectedSortOption.value)}`;
    }
    return filterSortText;
  });

  public queryText = computed(() =>
    this.getSortAndFilterText()
      .replaceAll(/Query./g, '')
      .replaceAll(/Sorting./g, '')
  );

  public hasFiltersSelected = computed(() => this.filterSortStateModel().selectedFilterOptions?.length > 0);

  public selectedSortOption = computed(() => this.filterSortStateModel().selectedSortOption);

  public matchesFilter(item: unknown): Signal<boolean> {
    return computed(() =>
      this.selectedFilterOptions()?.every(filterSetting => {
        if (!filterSetting.values?.length) return true;
        const key = TinyHelpers.uncapitalize(filterSetting.key);
        return filterSetting.values.some(value => value.toLowerCase() === item?.[key]?.toString()?.toLowerCase());
      })
    );
  }

  setFilterValues(payload: { values: string[]; index: number; allItemsSelected?: boolean }): void {
    this.filterSortStateModel.update(state =>
      produce(state, draft => {
        draft.selectedFilterOptions[payload.index].values = payload.values;
      })
    );
  }

  setReloadNeeded(isReloadNeeded: boolean): void {
    this.filterSortStateModel.update(state =>
      produce(state, draft => {
        draft.isReloadNeeded = isReloadNeeded;
      })
    );
  }

  removeFilterOptionByKey(filterOptionKey: FilterOptions): void {
    this.filterSortStateModel.update(state =>
      produce(state, draft => {
        draft.selectedFilterOptions = draft.selectedFilterOptions.filter(filter => filterOptionKey !== filter.key);
      })
    );
  }

  removeFilterOption(filterOption: FilterSetting): void {
    this.filterSortStateModel.update(state =>
      produce(state, draft => {
        draft.selectedFilterOptions = draft.selectedFilterOptions.filter(filter => filterOption.key !== filter.key);
      })
    );
  }

  setFilterOptions(filterOptions: FilterSetting[]): void {
    this.filterSortStateModel.update(state =>
      produce(state, draft => {
        draft.selectedFilterOptions = filterOptions.map(option => {
          const previousValues = draft.selectedFilterOptions.find(selection => selection.key === option.key)?.values;

          return { key: option.key, values: previousValues || option.values || null };
        });
      })
    );
  }

  setFilterOptionsFromText(text: string): void {
    const params = new HttpParams({ fromString: text });

    const selectedFilterOptions: FilterSetting[] = params
      .keys()
      .filter(key => key)
      .map((paramKey: FilterOptions) => {
        const key = paramKey.replaceAll(/Query./g, '') as FilterOptions;
        const values = params.getAll(paramKey);
        return { key, values };
      });
    this.filterSortStateModel.update(state => ({ ...state, selectedFilterOptions }));
  }

  setPreservedFilterOptionsFromText(text: string): void {
    const params = new HttpParams({ fromString: text });

    const selectedFilterOptions: FilterSetting[] = params
      .keys()
      .filter(key => key)
      .map((paramKey: FilterOptions) => {
        const key = paramKey.replaceAll(/Query./g, '') as FilterOptions;
        const values = params.getAll(paramKey);
        return { key, values };
      });

    this.filterSortStateModel.update(state =>
      produce(state, draft => {
        const existing = draft.selectedFilterOptions;
        const newKeys = selectedFilterOptions.map(f => f.key);
        const preserved = existing.filter(f => !newKeys.includes(f.key));
        draft.selectedFilterOptions = [...preserved, ...selectedFilterOptions];
      })
    );
  }

  setSortOption(sortOption: SortingSetting): void {
    this.filterSortStateModel.update(state =>
      produce(state, draft => {
        draft.selectedSortOption = sortOption;
      })
    );
  }

  resetFilterOptions(): void {
    this.filterSortStateModel.update(state =>
      produce(state, draft => {
        draft.selectedFilterOptions = [];
      })
    );
  }

  resetSortOptions(): void {
    this.filterSortStateModel.update(state =>
      produce(state, draft => {
        draft.selectedSortOption = null;
      })
    );
  }

  resetSortAndFilterOptions(): void {
    this.filterSortStateModel.update(state =>
      produce(state, draft => {
        draft.selectedSortOption = null;
        draft.selectedFilterOptions = [];
      })
    );
  }

  /** Invalidates and reloads filter options
   *  @param keys if not defined, invalidates all options
   */
  invalidateFilterOptions(keys?: string[]): void {
    this.invalidateFilterOptionsSuccessful$.next(keys);
  }
}
